// src/pages/PriceList.jsx
import React, { useMemo, useEffect } from "react";
import Meta from "../components/Meta";
import Container from "../components/Container";
import { Link } from "react-router-dom";

import "./styles/priceList.css";

const PriceList = () => {
  // ✅ Add a page-scoped body class to disable inner/main scroll just for this page
  useEffect(() => {
    document.body.classList.add("ku-pl-page");
    return () => document.body.classList.remove("ku-pl-page");
  }, []);

  const sections = useMemo(
    () => [
      {
        title: "Digital Printing",
        sub: "Fast, crisp prints for documents & photos",
        items: [
          { name: "A4 page / Artboard", price: "400 / 500" },
          { name: "A4 photo", price: "500" },
          { name: "A4 extended solid colour", price: "600" },
          { name: "A3 page / Artboard", price: "800 / 1000" },
          { name: "A3 photo", price: "1000" },
          { name: "A3 extended solid colour", price: "1200" },
          { name: "A2 BW / colour", price: "20,000 / 30,000" },
          { name: "A0 BW / colour", price: "40,000 / 60,000" },
          { name: "Canvas book A4", price: "20,000" },
          { name: "A2", price: "40,000" },
          { name: "A1", price: "50,000" },
          { name: "A0", price: "60,000" },
        ],
        ctaText: "Print Now",
        ctaTo: "/print",
      },
      {
        title: "Office Stationery",
        sub: "Cards, letterheads, envelopes & more",
        items: [
          { name: "Letterheads (normal paper)", price: "150" },
          { name: "Letterheads (clients paper)", price: "120" },
          { name: "Business cards (round edges)", price: "200" },
          { name: "Notebook A5 (min 100 copies)", price: "3,000" },
          { name: "Envelopes A5", price: "250 / 500" },
          { name: "Envelopes A4", price: "250 / 500" },
          { name: "Envelopes A2", price: "300 / 800" },
          { name: "Envelopes A3", price: "500 / 1200" },
          { name: "Receipt book A4", price: "20,000" },
          { name: "Receipt book A5", price: "15,000" },
          { name: "Table calendars", price: "10,000" },
          { name: "A2 paper folders (min 500 copies)", price: "4,000" },
        ],
        featured: true,
        ctaText: "Contact Us",
        ctaTo: "/contact",
      },
      {
        title: "Large Format",
        sub: "Banners, stickers, foamboard & signage",
        items: [
          { name: "Sticker glossy (sqm)", price: "20,000" },
          { name: "Wall paper (sqm)", price: "60,000" },
          { name: "Banner (sqm)", price: "25,000" },
          { name: "Pull up banner", price: "250,000" },
          { name: "Tear drop banner", price: "300,000" },
          { name: "Foamboard signs (sqm)", price: "52,500" },
          { name: "Flag 90cm x 150cm", price: "65,000" },
        ],
        ctaText: "Get Quote",
        ctaTo: "/contact",
      },

      // Extra sections (render as smaller glass cards)
      {
        title: "Sticker Label Paper",
        sub: "Self-adhesive label sheets",
        compact: true,
        items: [
          { name: "A4", price: "600" },
          { name: "A3", price: "1,200" },
        ],
      },
      {
        title: "Soft Lamination",
        sub: "Clean, protective finishing",
        compact: true,
        items: [
          { name: "A4 per page", price: "150" },
          { name: "A3 per page", price: "250" },
        ],
      },
      {
        title: "Booklets",
        sub: "Finished product (cover + inners)",
        compact: true,
        note:
          "Price includes up to 300gsm cover. Choose 130gsm for inners, plus lamination of your choice.",
        items: [
          { name: "A4 booklet per page", price: "600" },
          { name: "A5 booklet per page", price: "300" },
        ],
      },
    ],
    []
  );

  const topCards = sections.filter((s) => !s.compact).slice(0, 3);
  const compactCards = sections.filter((s) => s.compact);

  return (
    <>
      <Meta title="Price List" />

      <Container class1="ku-pl-wrap">
        <div className="ku-pl-shell">
          {/* ===== TOP NAV (like reference) ===== */}
          <div className="ku-pl-topbar">
            <div className="ku-pl-tabs" role="navigation" aria-label="Price list tabs">
              <a className="ku-pl-tab" href="#pricing">
                Pricing
              </a>
              <a className="ku-pl-tab" href="#extras">
                Extras
              </a>

              <Link className="ku-pl-tab" to="/contact">
                Contact
              </Link>
            </div>

            <Link to="/print" className="ku-pl-download">
              Print Now
            </Link>
          </div>

          {/* ===== BIG HERO ===== */}
          <header className="ku-pl-hero" id="pricing">
            <div className="ku-pl-heroText">
              <h1 className="ku-pl-heroTitle" aria-label="Pricing">
                Pricing
              </h1>
              <p className="ku-pl-heroSub">
                Transparent UGX pricing for prints, stationery and signage. Need something custom?
                We’ll quote in minutes.
              </p>

              <div className="ku-pl-metaRow">
                <span className="ku-pl-pill">Prices in UGX</span>
                <span className="ku-pl-muted">
                  Call/WhatsApp:{" "}
                  <a className="ku-pl-link" href="tel:+256200949142">
                    +256 200 949 142
                  </a>
                </span>
                <span className="ku-pl-dot" aria-hidden="true" />
                <span className="ku-pl-muted">
                  Email:{" "}
                  <a className="ku-pl-link" href="mailto:kupto2020@gmail.com">
                    kupto2020@gmail.com
                  </a>
                </span>
              </div>
            </div>
          </header>

          {/* ===== 3 GLASS PRICING CARDS ===== */}
          <section className="ku-pl-cards" aria-label="Price list cards">
            {topCards.map((sec) => (
              <article
                key={sec.title}
                className={`ku-pl-card ${sec.featured ? "is-featured" : ""}`}
              >
                <div className="ku-pl-cardGlow" aria-hidden="true" />
                <div className="ku-pl-cardInner">
                  <div className="ku-pl-cardTop">
                    <span className="ku-pl-planTag">
                      {sec.featured ? "Most Popular" : "Category"}
                    </span>
                    <h2 className="ku-pl-planTitle">{sec.title}</h2>
                    <p className="ku-pl-planSub">{sec.sub}</p>
                  </div>

                  <div className="ku-pl-list">
                    {sec.items.map((it, idx) => (
                      <div className="ku-pl-row" key={`${sec.title}-${idx}`}>
                        <span className="ku-pl-check" aria-hidden="true">
                          ✓
                        </span>
                        <span className="ku-pl-name">{it.name}</span>
                        <span className="ku-pl-spacer" aria-hidden="true" />
                        <span className="ku-pl-price">{it.price}</span>
                      </div>
                    ))}
                  </div>

                  <div className="ku-pl-cardBottom">
                    <Link
                      to={sec.ctaTo || "/print"}
                      className={`ku-pl-cta ${sec.featured ? "is-primary" : ""}`}
                    >
                      {sec.ctaText || "Get Started"}
                    </Link>

                    {!sec.featured ? (
                      <Link to="/contact" className="ku-pl-ctaGhost">
                        Ask a Question
                      </Link>
                    ) : null}
                  </div>
                </div>
              </article>
            ))}
          </section>

          {/* ===== EXTRAS (smaller glass cards) ===== */}
          <section className="ku-pl-extras" id="extras" aria-label="Extra pricing sections">
            <div className="ku-pl-extrasHead">
              <h3 className="ku-pl-extrasTitle">Extras</h3>
              <p className="ku-pl-extrasSub">
                Add-ons and finishing options (lamination, labels, booklets).
              </p>
            </div>

            <div className="ku-pl-extrasGrid">
              {compactCards.map((sec) => (
                <article key={sec.title} className="ku-pl-miniCard">
                  <div className="ku-pl-miniTop">
                    <h4 className="ku-pl-miniTitle">{sec.title}</h4>
                    <p className="ku-pl-miniSub">{sec.sub}</p>
                  </div>

                  <div className="ku-pl-miniList">
                    {sec.items.map((it, idx) => (
                      <div className="ku-pl-miniRow" key={`${sec.title}-mini-${idx}`}>
                        <span className="ku-pl-miniName">{it.name}</span>
                        <span className="ku-pl-miniDots" aria-hidden="true" />
                        <span className="ku-pl-miniPrice">{it.price}</span>
                      </div>
                    ))}
                  </div>

                  {sec.note ? <p className="ku-pl-miniNote">{sec.note}</p> : null}
                </article>
              ))}
            </div>
          </section>

          {/* ===== FAQ teaser ===== */}
          <section className="ku-pl-faq" id="faq" aria-label="FAQ teaser">
            <div className="ku-pl-faqCard">
              <div className="ku-pl-faqText">
                <h3 className="ku-pl-faqTitle">Need help choosing?</h3>
                <p className="ku-pl-faqSub">
                  Tell us size, quantity, paper type and deadline — we’ll recommend the best option.
                </p>
              </div>
              <div className="ku-pl-faqActions">
                <Link className="ku-pl-faqBtn" to="/help">
                  Help Center
                </Link>
                <Link className="ku-pl-faqBtn is-solid" to="/contact">
                  Contact Support →
                </Link>
              </div>
            </div>
          </section>

          {/* glows */}
          <span className="ku-pl-bgGlow ku-pl-bgGlow--1" />
          <span className="ku-pl-bgGlow ku-pl-bgGlow--2" />
          <span className="ku-pl-bgGlow ku-pl-bgGlow--3" />
        </div>
      </Container>
    </>
  );
};

export default PriceList;
